/**
 * EducationEdit Component
 * 
 * Description: This component allows users to manage their education history. It provides a form for entering details about educational institutions, courses, start and end dates. Users can add or remove education entries and submit updates to the server. The component fetches initial data from the Redux store and updates it upon successful submission.
 * 
 * File Name: EducationEdit.tsx
 * Date: 02-09-2024
 */

import React, { FormEvent, useEffect, useState } from 'react';
import { RootState } from '@store/store';
import moment from 'moment';
import { updateDoctorField } from '../../../Store/doctor';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PageHeader from 'src/Components/Customer/PageHeader';
import PreviewProfileButton from 'src/Components/Customer/PreviewProfileButton';
import PrimaryBtn from 'src/Components/GlobalComponents/primaryBtn';
import SimpleInput from 'src/Components/SimpleInput';
import httpRequest from 'src/helpers/httpRequest';
import { setUniqueData } from "src/Store/reducer";
import { IEducationsHistory } from 'src/types/GlobalInterfaces';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';

const emptyObj: IEducationsHistory = {
    institution: "",
    course: "",
    startDate: "",
    endDate: "",
};

const EducationEdit: React.FC = () => {
    const { profile } = useSelector((store: RootState) => store.doctor);
    const dispatch = useDispatch();
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [education, setEducation] = useState<IEducationsHistory[]>([]);

    // API_CALL: Updates the user's "education" section with new data
    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!payload.institution.trim() || !payload.course.trim()) {
            toast("Institution and course are required", { type: "error" });
            return;
        }

        if(payload.institution.trim().length > 60 || payload.institution.trim().length < 3) {
            toast("Institution must be between 3 and 60 characters", { type: "error" });
            return;
        }

        if(payload.course.trim().length > 60 || payload.course.trim().length < 3) {
            toast("Course must be between 3 and 60 characters", { type: "error" });
            return;
        }


        if (!payload.startDate.trim() || !payload.endDate.trim()) {
            toast("Start and end date are required", { type: "error" });
            return;
        }

        if (payload.startDate > payload.endDate) {
            toast("Start date must be before end date", { type: "error" });
            return;
        }
        if (payload.startDate > moment().format("YYYY-MM-DD")) {
            toast("Start date cannot be in the future", { type: "error" });
            return;
        }

        setIsLoader(true);
        const newArr = (editId || editId === 0) ?
            education.map((v, i) => i === editId ? payload : v)
            :
            [...education, payload];
        const { res, err, formErrors } = await httpRequest<{ message: string; data: IEducationsHistory[]; percent: number }>({
            method: "post",
            path: "/provider/education",
            params: { education: newArr }
        });
        if (res) {
            const { message, data, percent } = res;
            toast(message, { type: "success" }); // api success message
            setCreateMode(false);
            setPayload(emptyObj);
            setEditId(null);
            dispatch(updateDoctorField({ education: data }));
            dispatch(setUniqueData({ key: 'profileCompletion', value: String(percent) }));
        } else {
            let errorMessage = err;
            if (formErrors) {
                errorMessage = formErrors?.education;
            }
            toast(errorMessage, { type: "error" });
        }
        setIsLoader(false);
    };

    const [editId, setEditId] = useState<null | number>(null);
    const editSetting = (obj: IEducationsHistory, id: number) => {
        setEditId(id);
        setPayload(obj);
        setCreateMode(true);
    };
    const cancelEdit = () => {
        setEditId(null);
        setPayload(emptyObj);
        setCreateMode(false);
    };

    const [deleteId, setDeleteId] = useState<null | number>(null);
    const handleDeleteRow = async (id: number) => {
        setDeleteId(id);
        const newArr = education.filter((_, i) => i !== id);
        const { res, err} = await httpRequest<{ data: IEducationsHistory[]; percent: number }>({
            method: "post",
            path: "/provider/education",
            params: { education: newArr }
        });
        if (res) {
            const { data, percent } = res;
            toast("Education history deleted.", { type: "success" }); // api success message
            setCreateMode(false);
            setPayload(emptyObj);
            setEditId(null);
            dispatch(updateDoctorField({ education: data }));
            dispatch(setUniqueData({ key: 'profileCompletion', value: String(percent) }));
        } else {
            let errorMessage = err;
            toast(errorMessage, { type: "error" });
        }
        setDeleteId(null);
    };

    const [createMode, setCreateMode] = useState<boolean>(false);
    const [payload, setPayload] = useState(emptyObj);
    const handleChange = (key: string, val: string) => {
        setPayload(prev => ({
            ...prev,
            [key]: val
        }));
    };
    useEffect(() => {
        if (profile) {
            if (!(profile?.education || [])?.length) {
                setCreateMode(true);
                setEducation([]);
            } else {
                // Create a copy of the array before sorting to avoid read-only error
                let educationArray = [...(profile?.education || [])];
                let sortedEducation = educationArray.sort((a, b) => {
                    return new Date(b?.endDate).getTime() - new Date(a?.endDate).getTime();
                });
                setEducation(sortedEducation);
            }
        }
    }, [profile]);
    return (
        <>
            <PageHeader
                title="Education"
                isButton={true}
                ButtonComponent={PreviewProfileButton}
            />
            <div className="parent-box minheight-parent-box">
                <p className="font-Poppins-semiBold font-size-18px theme-color-green mb-2">If you have undergone education and training including degrees, courses, diplomas and other qualifications enter them below.</p>
                {/* 19/12/2024 */}
                {education?.length ?
                    education.map((item: IEducationsHistory, index: number) => (
                        <div key={index} className={`eduction-work-his-change ${editId === index ? "eduction-work-his-change-active" : ""}`}>
                            <div className="absolute flex gap-4 right-4 top-4">
                                <FontAwesomeIcon
                                    icon={faPen}
                                    className="cursor-pointer font-size-20px text-[#707070]"
                                    onClick={() => editSetting(item, index)}
                                    id={`edit-education-pen-icon-${index + 1}`}
                                />
                                <FontAwesomeIcon
                                    icon={deleteId === index ? faSpinner : faTrash}
                                    spin={deleteId === index}
                                    className="cursor-pointer font-size-20px text-[#707070]"
                                    onClick={() => handleDeleteRow(index)}
                                    id={`edit-education-delete-icon-${index + 1}`}
                                />
                            </div>
                            <div className="flex gap-10">
                                <div className=" eduction-work-his-change-inner min-w-[20%]">
                                    <div>
                                        <span className="font-size-18px theme-color-green font-Poppins-Medium block">Institution</span>
                                        <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">{item?.institution || ""}</span>
                                    </div>
                                    <div>
                                        <span className="font-size-18px theme-color-green font-Poppins-Medium block">Start Date</span>
                                        <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">{moment(item?.startDate || "").format("DD-MM-YYYY")}</span>
                                    </div>
                                </div>
                                <div className="eduction-work-his-change-inner">
                                    <div>
                                        <span className="font-size-18px theme-color-green font-Poppins-Medium block">Degree/Course</span>
                                        <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">{item?.course || ""}</span>
                                    </div>
                                    <div>
                                        <span className="font-size-18px theme-color-green font-Poppins-Medium block">End Date</span>
                                        <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">{moment(item?.endDate || "").format("DD-MM-YYYY")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                    :
                    null
                }
                {createMode &&
                    <form className='work-history-ui-adjustment' onSubmit={handleSubmit}>
                        <SimpleInput
                            value={payload?.institution || ""}
                            onChange={(val) => handleChange("institution", val)}
                            isLabel={true}
                            labelText={"Institution"}
                            isRequired={true}
                            maxLength={60}
                            extraStyle={{ borderColor: "#009d91" }}
                            id="edit-education-institution"
                        />
                        <SimpleInput
                            value={payload?.course || ""}
                            onChange={(val) => handleChange("course", val)}
                            isLabel={true}
                            labelText={"Degree/Course"}
                            isRequired={true}
                            maxLength={60}
                            extraStyle={{ borderColor: "#009d91" }}
                            id="edit-education-degree-course"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <SimpleInput
                                    value={moment(payload?.startDate).format("YYYY-MM-DD")}
                                    onChange={(val) => handleChange("startDate", `${val}T00:00:00Z`)}
                                    isLabel={true}
                                    labelText={"Start Date"}
                                    isRequired={true}
                                    extraStyle={{ borderColor: "#009d91" }}
                                    type='date'
                                    max={moment().format("YYYY-MM-DD")}  // Disable future dates for start date
                                    id="edit-education-start-date"
                                />
                            </div>
                            <div>
                                <SimpleInput
                                    value={moment(payload?.endDate).format("YYYY-MM-DD")}
                                    onChange={(val) => handleChange("endDate", `${val}T00:00:00Z`)}
                                    isLabel={true}
                                    labelText={"End Date"}
                                    isRequired={true}
                                    extraStyle={{
                                        borderColor: "#009d91",
                                        cursor: payload?.startDate ? "pointer" : "not-allowed"
                                    }}
                                    type='date'
                                    min={moment(payload?.startDate).format("YYYY-MM-DD")}  // Set min date for end date to start date
                                    max={moment().format("YYYY-MM-DD")}  // Disable future dates for end date
                                    disabled={!payload?.startDate}  // Disable end date input until start date is selected
                                    title={payload?.startDate ? "" : "Please select the start date first."} // if disable then show title
                                    id="edit-education-end-date"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-5 gap-4">
                            {(editId || editId === 0) &&
                                <PrimaryBtn
                                    type="button"
                                    ButtonText="Cancel"
                                    btnExtraStyle={{
                                        borderRadius: "1.5rem",
                                        backgroundColor: "#ff0000"
                                    }}
                                    onPress={cancelEdit}
                                    id="edit-education-cancel-btn"
                                />
                            }
                            <PrimaryBtn
                                loader={isLoader}
                                type="submit"
                                ButtonText={"Save"}
                                btnExtraStyle={{
                                    borderRadius: "1.5rem",
                                    backgroundColor: "#004540"
                                }}
                                id="edit-education-save-btn"
                            />
                        </div>
                    </form>
                }
                {!createMode &&
                    <div className="my-5">
                        <button
                            onClick={() => setCreateMode(true)}
                            type="button"
                            className="font-Poppins-semiBold font-size-18px theme-color-green"
                            id="edit-education-add-btn"
                        >
                            + Add another course/degree
                        </button>
                    </div>
                }
            </div>
        </>
    );
};

export default EducationEdit;